import PropTypes from 'prop-types'
import React from 'react'
import { injectStripe } from 'react-stripe-elements'

import ButtonApplePay from 'components/ButtonApplePay'
import ButtonGooglePay from 'components/ButtonGooglePay'
import ButtonMsPay from 'components/ButtonMsPay'
import { gtmEvent } from 'utils/client/analitics'
import Sentry from 'utils/sentry'

import WebPaymentBtn from './components/WebPaymentBtn'

const PaymentButtons = {
  Apple: ButtonApplePay,
  Google: ButtonGooglePay,
  Ms: ButtonMsPay,
  ['Saved Methods']: WebPaymentBtn,
}

const PaymentRequestButton = ({
  stripe,
  handleSuccess,
  cart,
  canMakePayment,
  paymentRequest,
  texts,
}) => {
  const buttonType = React.useMemo(
    () =>
      canMakePayment?.applePay
        ? 'Apple'
        : canMakePayment?.googlePay
        ? 'Google'
        : canMakePayment?.msPay
        ? 'Ms'
        : 'Saved Methods',
    [canMakePayment],
  )

  React.useEffect(() => {
    gtmEvent({
      event: `${buttonType} Button - Showed`,
    })
  }, [buttonType])

  React.useEffect(() => {
    paymentRequest?.on('cancel', () => {
      gtmEvent({
        event: `${buttonType} Payment - Canceled`,
      })
    })
  }, [paymentRequest, buttonType])

  React.useEffect(() => {
    if (canMakePayment) {
      paymentRequest?.on('paymentmethod', async ev => {
        const { error: confirmError } = await stripe.confirmCardPayment(
          cart.clientSecret,
          { payment_method: ev.paymentMethod.id },
          { handleActions: false },
        )

        if (confirmError) {
          ev.complete('fail')

          gtmEvent({
            event: `${buttonType} Payment - Error`,
            description: confirmError,
          })

          Sentry.captureMessageWithScope('STRIPE: PAYMENT REQUEST FAIL', {
            error: confirmError,
          })
        } else {
          ev.complete('success')
          handleSuccess()

          gtmEvent({
            event: `${buttonType} Payment - Success`,
          })
        }
      })
    }
  }, [
    canMakePayment,
    cart.clientSecret,
    handleSuccess,
    paymentRequest,
    stripe,
    buttonType,
  ])

  const onClick = React.useCallback(
    e => {
      gtmEvent({
        event: `Button ${buttonType} - Clicked`,
      })

      try {
        paymentRequest?.show(e)
      } catch (e) {
        gtmEvent({
          event: `${buttonType} Payment - Error`,
          description: JSON.stringify(e),
        })
      }
    },
    [paymentRequest, buttonType],
  )

  const PaymentBtn = React.useMemo(() => PaymentButtons[buttonType], [
    buttonType,
  ])

  return <PaymentBtn title={texts.payment.button} onClick={onClick} />
}

PaymentRequestButton.propTypes = {
  stripe: PropTypes.object,
  handleSuccess: PropTypes.func.isRequired,
  cart: PropTypes.object.isRequired,
  canMakePayment: PropTypes.object.isRequired,
  paymentRequest: PropTypes.object.isRequired,
  texts: PropTypes.object.isRequired,
}

export default injectStripe(PaymentRequestButton)
